import React from 'react';
import { OpeningEntityTypeEnum, OpeningModeEnum } from '@api/ringil-api.v2.tsx';
import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import SvgMapPin from '@components/framework/icons/MapPin.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import LocationSelect from '@components/framework/locationselect/LocationSelect.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import RingilTextArea from '@components/framework/input/RingilTextArea.tsx';
import ReadonlyInput from '@components/framework/input/ReadonlyInput/ReadonlyInput.tsx';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import InputContact from '@components/framework/InputContact/InputContact.tsx';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { InputCheckbox } from '@components/ringil3/Input/InputCheckbox/InputCheckbox.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgIconPlus from '@components/framework/orders/IconPlus.tsx';
import SvgCoButtonDelete from '@icons/CoButtonDelete.tsx';
import ManageTimeslot from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import { TimezoneAutoResolve } from '@hooks/useResolveTimezone/TimezoneAutoResolve.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { findValidationMessage } from '@utils/validation.tsx';
import { findErrorMessage } from '@utils/validationUtils.ts';
import { formatAddress } from '@utils/formators.tsx';
import { isNotBlank, isNotNullOrUndefined } from '@utils/utils.tsx';
import { buildCarrierNoteWithLocationBlock } from '@utils/carrierNoteMerge.ts';
import { Form } from '@hooks/useForm/useForm.tsx';
import { ExpeditionLocality, LocalityEntryModeEnum, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { RouteTestIds } from '@app/pages/expedition/create/testIds.ts';
import { toLocationNoteStops } from '@app/pages/expedition/create/mappers/detailToForm.ts';
import NotifyOfTransportMultiSelect from '@app/pages/expedition/create/components/NotifyOfTransportMultiSelect.tsx';
import { orientLineRoute } from '@app/pages/expedition/create/mappers/lineRoute.ts';

interface ComponentProps {
  form: Form<NewExpeditionForm>;
  locIndex: number;
  location: ExpeditionLocality;
  testIds: RouteTestIds;
}

/**
 * One stop of the route: where it is, who to contact there, and when it can be served.
 *
 * A stop is either picked from the address book (`FROM_LOCATIONS`) or typed in by hand
 * (`MANUALLY`); switching between the two carries over whatever the other mode can hold.
 */
const RouteLocationCard: React.FC<ComponentProps> = ({ form, locIndex, location: loc, testIds }) => {
  const { tr } = useTranslationLgs();
  // Set only by the Linka prefill: a locked or limited stop has to stay on an address-book location.
  const locationLocked = loc.locationLock === 'LOCKED';
  const locationLimited = loc.locationLock === 'LIMITED';

  return (
    <Card>
      <div className={'flex flex-col gap-4'}>
        <COHeadline title={tr('CreateExpedition.lokalita', 'Location')} variant={'h4'} icon={<SvgMapPin />} />
        {!locationLocked && !locationLimited && (
        <InputSelect
          selectedItemCode={loc.localityMode}
          model={[
            {
              label: tr('CreateExpedition.existujici', 'Existing'),
              code: LocalityEntryModeEnum.FROM_LOCATIONS,
            },
            {
              label: tr('CreateExpedition.zadatRucne', 'Enter manually'),
              code: LocalityEntryModeEnum.MANUALLY,
            },
          ]}
          onSelect={v => {
            if (v === loc.localityMode) return;
            form.useProduce(draft => {
              if (v === LocalityEntryModeEnum.MANUALLY) {
                // Carry the address-book entry over into the manual fields so nothing is retyped.
                const existingLocation = draft.locations[locIndex].location;
                const selectedPerson = draft.locations[locIndex].selectedPerson;
                const additionalContacts = draft.locations[locIndex].additionalContacts || [];

                const mainContactShouldBeNotified = additionalContacts.some(contact => contact.contactEmail === selectedPerson?.contactEmail);

                draft.locations[locIndex].manualAddress = {
                  name: existingLocation?.locationName || '',
                  postalCode: existingLocation?.addressTemplate?.postalCode || '',
                  countryCode3: existingLocation?.addressTemplate?.country || '',
                  city: existingLocation?.addressTemplate?.city || '',
                  streetNr: existingLocation?.addressTemplate?.streetNr || '',
                  mainContactPerson: selectedPerson?.contactName || '',
                  mainContactPhone: selectedPerson?.contactPhone || '',
                  mainContactEmail: selectedPerson?.contactEmail || '',
                  notifySelectedPerson: mainContactShouldBeNotified,
                  additionalPersons: [
                    ...additionalContacts
                      .filter(contact => contact.contactEmail)
                      .map(contact => ({
                        name: contact.contactName || '',
                        email: contact.contactEmail || '',
                      })),
                    ...(draft.locations[locIndex].additionalPersons || []),
                  ],
                  timezoneId: null,
                };
                draft.locations[locIndex].additionalPersons = [];
                draft.locations[locIndex].additionalContacts = [];
                draft.locations[locIndex].location = null;
                draft.locations[locIndex].companyLocationId = null;

                // Opening hours came from the address-book location, so only the chosen days
                // survive — the intervals have to be entered again for a manual address.
                const existingDates = draft.locations[locIndex].timeslots?.dayWithInterval?.map(day => day.day) || [];
                draft.locations[locIndex].timeslots = {
                  dayWithInterval: existingDates.map(date => ({
                    day: date,
                    openingMode: OpeningModeEnum.CLOSED,
                    intervals: [] as any[],
                  })),
                };
              } else {
                draft.locations[locIndex].additionalPersons = draft.locations[locIndex].manualAddress?.additionalPersons || [];
                draft.locations[locIndex].manualAddress = null;
              }
              draft.locations[locIndex].localityMode = v as LocalityEntryModeEnum;
            });
          }}
          dataTestIdPrefix={testIds.locationSelect}
        />
        )}

        {loc.localityMode === LocalityEntryModeEnum.FROM_LOCATIONS && (
          <>
            <Field
              label={tr('CreateExpedition.lokace', 'Location')}
              required
              errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].companyLocationId`, tr)}
            >
              <LocationSelect
                fullWidth
                value={loc.location?.locationId}
                dataTestId={testIds.locationSelect}
                readonly={locationLocked}
                allowedLocationIds={locationLimited ? loc.allowedLocationIds : undefined}
                onChange={v => {
                  const firstContact = v.contacts?.find((_, index) => index == 0);

                  form.useProduce(draft => {
                    // a both-direction Linka turns around when the first stop gets a location of its last stop
                    if (locIndex === 0) orientLineRoute(draft, v.locationId);
                    draft.locations[locIndex].location = v;
                    draft.locations[locIndex].companyLocationId = v.locationId;
                    if (isNotNullOrUndefined(firstContact)) {
                      draft.locations[locIndex].selectedPerson = {
                        contactEmail: firstContact.contactEmail?.trim() || null,
                        contactName: firstContact.contactName?.trim() || null,
                        contactPhone: firstContact.contactPhone?.trim() || null,
                      };
                    }
                    draft.locations[locIndex].location.contacts = v.contacts?.filter(contact => isNotBlank(contact.contactEmail)) || [];
                    // a Linka stop's own note wins over the address-book one, as on a locked stop
                    const stopNote = draft.locations[locIndex].tmplV2StopLocationNote;
                    if (isNotBlank(stopNote)) draft.locations[locIndex].location.locationNote = stopNote;
                    draft.locations[locIndex].additionalContacts =
                      v.contacts?.filter(contact => isNotNullOrUndefined(contact.contactEmail)).filter(contact => contact.automaticPrefillToNotify) || [];
                    draft.locations[locIndex].timezoneId = v.timezoneId;
                    draft.locations[locIndex].timeslots.timezoneId = v.timezoneId;
                    draft.carrierNote = buildCarrierNoteWithLocationBlock(draft.carrierNote, toLocationNoteStops([draft.locations[locIndex]]));
                  });
                }}
              />
            </Field>
            {/* The prefill swapped a superseded location for its replacement — say so rather than silently changing the stop. */}
            {loc.supersededFromLocationName && (
              <div className={'text-sm text-complementary-warning'} data-testid={`${testIds.locationSelect}-superseded`}>
                {tr('CreateExpedition.locationSupersededMarker', 'was {{oldName}}, now {{newName}}', {
                  oldName: loc.supersededFromLocationName,
                  newName: loc.location?.locationName,
                })}
              </div>
            )}
            {loc.location?.timezoneId && <COTimezone timezoneId={loc.location?.timezoneId} dataTestId={testIds.locationTimezone} />}
            {loc.location && !loc.location?.timezoneId && (
              <Field required label={tr('generic.timezoneId', 'Time zone')}>
                <InputTimezone
                  value={loc.timezoneId}
                  onChange={v =>
                    form.useProduce(d => {
                      d.locations[locIndex].timezoneId = v;
                    })
                  }
                />
              </Field>
            )}
            {isNotNullOrUndefined(loc.location) && (
              <Field label={tr('CreateExpedition.adresa', 'Address')} required>
                <ReadonlyInput
                  value={formatAddress(
                    loc.location.addressTemplate.country,
                    loc.location.addressTemplate.postalCode,
                    loc.location.addressTemplate.city,
                    loc.location.addressTemplate.streetNr,
                  )}
                  dataTestId={testIds.locationSelected}
                />
              </Field>
            )}
            <Field
              label={tr('CreateExpedition.hlavniKontakt', 'Main contact')}
              errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].selectedPerson`, tr)}
            >
              <InputContact
                contacts={loc.location?.contacts}
                value={loc?.selectedPerson ? loc?.location?.contacts?.find(contact => contact.contactEmail === loc.selectedPerson.contactEmail) : null}
                onChange={v => form.setFieldValue(`locations[${locIndex}].selectedPerson`, v)}
                fullWidth
                dataTestId={testIds.locationMainContact}
                dataDetailTestId={testIds.locationMainContactDetail}
              />
            </Field>
            <Field label={tr('CreateExpedition.notifikovatOPreprave', 'Notify about shipment')}>
              <NotifyOfTransportMultiSelect
                options={loc.location?.contacts}
                value={loc.additionalContacts}
                dataTestId={testIds.locationNotifyOfTransport}
                onChange={value => form.setFieldValue(`locations[${locIndex}].additionalContacts`, value)}
              />
            </Field>
            {loc.additionalPersons?.map((person, personIndex) => {
              return (
                <React.Fragment key={personIndex}>
                  <div className={'grid grid-cols-2 gap-4'}>
                    <Field label={tr('CreateExpedition.jmenoOsoby', 'Name of person')} required>
                      <RingilInputText
                        value={person.name}
                        dataTestId={`${testIds.locationNotifyOfTransport}-additional-${personIndex}-name`}
                        onChange={v =>
                          form.useProduce(draft => {
                            draft.locations[locIndex].additionalPersons[personIndex].name = v;
                          })
                        }
                        fullWidth
                      />
                    </Field>
                    <Field
                      label={tr('CreateExpedition.notifikovatEmailemOPreprave', 'Notify by email')}
                      required
                      errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].additionalPersons[${personIndex}].email`, tr)}
                      actionButtons={
                        <RingilButton
                          mode={'text-only'}
                          size={'small'}
                          icon={<SvgCoButtonDelete />}
                          label=""
                          dataTestId={`${testIds.locationNotifyOfTransport}-additional-${personIndex}-delete`}
                          onClick={() => {
                            form.useProduce(draft => {
                              draft.locations[locIndex].additionalPersons = draft.locations[locIndex].additionalPersons.filter((_, i) => i !== personIndex);
                            });
                          }}
                        />
                      }
                    >
                      <RingilInputText
                        value={person.email}
                        dataTestId={`${testIds.locationNotifyOfTransport}-additional-${personIndex}-email`}
                        onChange={v =>
                          form.useProduce(d => {
                            d.locations[locIndex].additionalPersons[personIndex].email = v;
                          })
                        }
                        fullWidth
                      />
                    </Field>
                  </div>
                </React.Fragment>
              );
            })}
            <LinkWithIcon
              label={tr('CreateExpedition.notifikovatDalsiEmail', 'Notify another email')}
              icon={<SvgIconPlus />}
              dataTestId={`${testIds.locationNotifyOfTransport}-addEmail`}
              onClick={() => {
                form.useProduce(draft => {
                  if (draft.locations[locIndex].additionalPersons == null) {
                    draft.locations[locIndex].additionalPersons = [];
                  }
                  draft.locations[locIndex].additionalPersons.push({
                    name: '',
                    email: '',
                  });
                });
              }}
            ></LinkWithIcon>
          </>
        )}
        {loc.localityMode === LocalityEntryModeEnum.MANUALLY && (
          <>
            <Field
              required
              label={tr('CreateExpedition.nazevLokace', 'Location name')}
              errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].manualAddress.name`, tr)}
            >
              <RingilInputText
                value={loc.manualAddress?.name}
                onChange={v =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.name = v;
                  })
                }
                fullWidth
                dataTestId={`${testIds.locationSelected}-manual-locationName`}
              />
            </Field>
            <div className={'grid grid-cols-2 gap-4'}>
              <Field
                label={tr('CreateExpedition.psc', 'Postal code')}
                required
                errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].manualAddress.postalCode`, tr)}
              >
                <RingilInputText
                  value={loc.manualAddress.postalCode}
                  onChange={v =>
                    form.useProduce(draft => {
                      draft.locations[locIndex].manualAddress.postalCode = v;
                    })
                  }
                  fullWidth
                  dataTestId={`${testIds.locationSelected}-manual-postalCode`}
                />
              </Field>
              <Field
                label={tr('CreateExpedition.zeme', 'Country')}
                required
                errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].manualAddress.countryCode3`, tr)}
              >
                <InputCountry
                  value={loc.manualAddress.countryCode3}
                  onChange={v => {
                    form.useProduce(d => {
                      d.locations[locIndex].manualAddress.countryCode3 = v;
                    });
                  }}
                  dataTestId={`${testIds.locationSelected}-manual-country`}
                />
              </Field>
            </div>
            <Field
              required
              label={tr('CreateExpedition.mesto', 'City')}
              errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].manualAddress.city`, tr)}
            >
              <RingilInputText
                value={loc.manualAddress?.city}
                onChange={v =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.city = v;
                  })
                }
                fullWidth
                dataTestId={`${testIds.locationSelected}-manual-city`}
              />
            </Field>
            <Field
              required
              label={tr('CreateExpedition.uliceCp', 'Street, number')}
              errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].manualAddress.streetNr`, tr)}
            >
              <RingilInputText
                value={loc.manualAddress?.streetNr}
                onChange={v =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.streetNr = v;
                  })
                }
                fullWidth
                dataTestId={`${testIds.locationSelected}-manual-street`}
              />
            </Field>
            <TimezoneAutoResolve
              countryCode3={loc.manualAddress.countryCode3}
              city={loc.manualAddress?.city}
              postalCode={loc.manualAddress?.postalCode}
              onResolved={timezoneId => {
                form.useProduce(d => {
                  d.locations[locIndex].manualAddress.timezoneId = timezoneId;
                  d.locations[locIndex].timezoneId = timezoneId;
                  if (d.locations[locIndex].timeslots) {
                    d.locations[locIndex].timeslots.timezoneId = timezoneId;
                  }
                });
              }}
            />
            <Field
              required
              label={tr('generic.timezoneId', 'Time zone')}
              errorMessage={findErrorMessage(form.validationErrors, `locations[${locIndex}].manualAddress.timezoneId`, tr)}
            >
              <InputTimezone
                value={loc.manualAddress?.timezoneId}
                onChange={v =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.timezoneId = v;
                    d.locations[locIndex].timezoneId = v;
                    if (d.locations[locIndex].timeslots) {
                      d.locations[locIndex].timeslots.timezoneId = v;
                    }
                  })
                }
                dataTestId={`${testIds.locationSelected}-manual-timezone`}
              />
            </Field>
            <COHeadline variant={'h4'} title={tr('CreateExpedition.kontakty', 'Contacts')} />
            <Field label={tr('CreateExpedition.hlavniKontaktJmeno', 'Main contact name')}>
              <RingilInputText
                value={loc.manualAddress?.mainContactPerson}
                onChange={v =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.mainContactPerson = v;
                  })
                }
                fullWidth
                dataTestId={`${testIds.locationSelected}-manual-mainContactPerson`}
              />
            </Field>
            <Field label={tr('CreateExpedition.hlavniKontaktniTelefon', 'Main contact phone')}>
              <RingilInputText
                value={loc.manualAddress?.mainContactPhone}
                onChange={v =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.mainContactPhone = v;
                  })
                }
                fullWidth
                dataTestId={`${testIds.locationSelected}-manual-mainContactPhone`}
              />
            </Field>
            <Field
              label={tr('CreateExpedition.hlavniKontaktniEmail', 'Main contact email')}
              errorMessage={findErrorMessage(form.validationErrors, `locations[${locIndex}].manualAddress.mainContactEmail`, tr)}
            >
              <RingilInputText
                value={loc.manualAddress?.mainContactEmail}
                onChange={v =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.mainContactEmail = v;
                  })
                }
                fullWidth
                dataTestId={`${testIds.locationSelected}-manual-mainContactEmail`}
              />
            </Field>
            {(loc.manualAddress.additionalPersons?.length > 0 || loc.manualAddress?.mainContactEmail) && (
              <COHeadline variant={'h4'} title={tr('CreateExpedition.notifikovatEmailemOPreprave', 'Notify by email')} />
            )}

            {loc.manualAddress.mainContactEmail && loc.manualAddress.mainContactPerson && (
              <InputCheckbox
                value={loc.manualAddress.notifySelectedPerson}
                label={tr('CreateExpedition.hlavniKontakt', 'Main contact')}
                note={loc.manualAddress.mainContactEmail}
                onChange={(_, v) =>
                  form.useProduce(d => {
                    d.locations[locIndex].manualAddress.notifySelectedPerson = v;
                  })
                }
              />
            )}
            {loc.manualAddress.additionalPersons?.map((person, personIndex) => {
              return (
                <React.Fragment key={personIndex}>
                  <div className={'grid grid-cols-2 gap-4'}>
                    <Field label={tr('CreateExpedition.jmenoOsoby', 'Name of person')} required>
                      <RingilInputText
                        value={person.name}
                        onChange={v =>
                          form.useProduce(draft => {
                            draft.locations[locIndex].manualAddress.additionalPersons[personIndex].name = v;
                          })
                        }
                        fullWidth
                      />
                    </Field>
                    <Field
                      label={tr('CreateExpedition.notifikovatEmailemOPreprave', 'Notify by email')}
                      required
                      errorMessage={findValidationMessage(
                        form.validationErrors,
                        `locations[${locIndex}].manualAddress.additionalPersons[${personIndex}].email`,
                        tr,
                      )}
                      actionButtons={
                        <RingilButton
                          mode={'text-only'}
                          size={'small'}
                          icon={<SvgCoButtonDelete />}
                          label=""
                          onClick={() => {
                            form.useProduce(draft => {
                              draft.locations[locIndex].manualAddress.additionalPersons = draft.locations[locIndex].manualAddress.additionalPersons.filter(
                                (_, i) => i !== personIndex,
                              );
                            });
                          }}
                        />
                      }
                    >
                      <RingilInputText
                        value={person.email}
                        onChange={v =>
                          form.useProduce(d => {
                            d.locations[locIndex].manualAddress.additionalPersons[personIndex].email = v;
                          })
                        }
                        fullWidth
                      />
                    </Field>
                  </div>
                </React.Fragment>
              );
            })}
            <LinkWithIcon
              label={tr('CreateExpedition.notifikovatDalsiEmail', 'Notify another email')}
              icon={<SvgIconPlus />}
              onClick={() => {
                form.useProduce(draft => {
                  if (draft.locations[locIndex].manualAddress.additionalPersons == null) {
                    draft.locations[locIndex].manualAddress.additionalPersons = [];
                  }
                  draft.locations[locIndex].manualAddress.additionalPersons.push({
                    name: '',
                    email: '',
                  });
                });
              }}
            ></LinkWithIcon>
          </>
        )}

        <Field label={tr('CreateExpedition.referenceProNakladku', 'Reference for loading')}>
          <RingilTextArea
            value={loc.loadingReference}
            dataTestId={testIds.locationLoadingReference}
            onChange={v => form.setFieldValue(`locations[${locIndex}].loadingReference`, v)}
            maxLength={1000}
            rows={1}
            className={'resize-none w-full'}
          />
        </Field>
        <Field
          label={tr('CreateExpedition.ocekavanaManipulace', 'Expected manipulation')}
          required
          errorMessage={findValidationMessage(form.validationErrors, `locations[${locIndex}].timeslots.dayWithInterval`, tr)}
        >
          <ManageTimeslot
            // Deliberately not keyed by locationId: remounting on every locality change would reset
            // ManageTimeslot's knowledge of which locality the picked days were derived against, and
            // it needs that to re-derive them against the newly picked one.
            key={`${loc.localityMode}-${locIndex}`}
            timeslot={loc.timeslots}
            externalLabel
            onChange={v => {
              form.setFieldValue(`locations[${locIndex}].timeslots`, {
                ...v,
                timezoneId: loc.location?.timezoneId || loc.manualAddress?.timezoneId,
              });
            }}
            // A stop prefilled from a Linka takes the line's weekly windows, when it has any.
            entityId={loc.tmplV2StopId && loc.tmplV2StopHasOpeningHours ? loc.tmplV2StopId : loc.location?.locationId}
            entityType={loc.tmplV2StopId && loc.tmplV2StopHasOpeningHours ? OpeningEntityTypeEnum.TMPL_V2_STOP : OpeningEntityTypeEnum.LOCATION}
            validationErrors={form.validationErrors}
            validationPrefix={`locations[${locIndex}].timeslots.`}
            value={loc.timeslots?.dayWithInterval}
            dataTestId={testIds.locationTimeslot}
          />
        </Field>
      </div>
    </Card>
  );
};

export default RouteLocationCard;